// What to show for an error from the agents API (backend/api/agents/views.py).
//
// authedFetch throws an Error whose message is the server's {"error": ...} text, with the HTTP
// status on err.status and the body on err.payload. That text is one of two kinds:
//  - a sentence, written for the user: shown as it comes;
//  - a code, a fixed token for the UI to map to its own words: never shown as it is.
// "Request failed (N)." is authedFetch's own text when the body had none (a 405, or Django's own 404
// for a path that isn't an id), and says nothing a person can use.
//
// The cards, the dialogs and the chat all show errors through agentErrorText.
const CODES = new Map([
  ['unauthenticated', 'Your session has expired. Reload the page, and sign in again if that does not fix it.'],
  ['forbidden', "Your account can't use Agents."],
  ['view_as_session_is_read_only', 'This is a view-as session, so nothing can be changed.'],
  ['not found', "That agent can't be found. It may have been deleted, or you may no longer be a member."],
  ['invalid JSON', 'Something went wrong.'],
]);
const GENERIC = 'Something went wrong.';
const REQUEST_FAILED = /^Request failed \(\d+\)\.$/;
// A 503 that trying again won't change: the feature is off, or the server isn't set up for it.
const WONT_PASS = /\b(switched off|aren't set up)\b/i;

const messageOf = (err) => (typeof err === 'string' ? err : err?.message);

export function agentErrorText(err) {
  const message = messageOf(err);
  if (typeof message !== 'string' || message.trim() === '' || REQUEST_FAILED.test(message)) return GENERIC;
  return CODES.get(message) ?? message;
}

// Whether a Retry is worth offering: a 503 that is a passing failure ("Agents are temporarily
// unavailable.", "Couldn't confirm access to schema A just now.", the planner couldn't reach the
// AI), not the feature being off.
//
// The server's 503s differ only in their sentence, so this reads it. It depends on two sentences
// in backend/api/agents/views.py, which WONT_PASS matches by their words: "Agents are switched off
// on this server." and NOT_SET_UP, "Agents aren't set up on this server yet.". Every other 503
// (UNAVAILABLE, the access check's "Couldn't confirm ... just now.", the planner's "Try again in a
// moment.") is taken to pass. Reword one of those two, or add a 503 that won't pass, and WONT_PASS
// and the test in agentErrors.test.js change with it.
export function agentErrorCanRetry(err) {
  const message = messageOf(err);
  return err?.status === 503 && !(typeof message === 'string' && WONT_PASS.test(message));
}
